import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { apiMessage, isConflict } from './LabBenchErrors';
import type { SheetItem } from './LabBenchResultInput';

export function LabBenchCriticalDialog({
  item,
  onChanged,
  onClose,
}: {
  item: SheetItem;
  onChanged: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const result = item.latestResult;
  const [toldName, setToldName] = useState('');
  const [readBackNote, setReadBackNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const told = toldName.trim();
  const note = readBackNote.trim();
  const valid = told.length >= 2 && note.length >= 3;

  const acknowledge = useMutation({
    mutationFn: () => post(`/lab/results/${result?.id}/acknowledge`, { toldName: told, readBackNote: note }),
    onSuccess: () => {
      toast.success(tr('lab', 'critical_value_acknowledged'), tr('lab', 'x_was_told_and_read_the_value_back', { told: told }));
      onChanged();
      onClose();
    },
    onError: (caught) => {
      setError(apiMessage(caught));
      if (isConflict(caught)) onChanged();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'acknowledge_critical_value')}
      description={result ? `${item.test.name}: ${result.value}${result.unit ? ` ${result.unit}` : ''}` : item.test.name}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              acknowledge.mutate();
            }}
            loading={acknowledge.isPending}
            disabled={!valid || !result}
          >
            {tr('lab', 'record_acknowledgement')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <Field label={tr('lab', 'who_was_told')} required hint={tr('lab', 'the_clinician_or_nurse_who_received_the_result')}>
          <Input value={toldName} maxLength={120} onChange={(e) => setToldName(e.target.value)} />
        </Field>
        <Field label={tr('lab', 'read_back_note')} required hint={tr('lab', 'what_they_read_back_to_you_and_how_phone_in')}>
          <Textarea rows={3} value={readBackNote} maxLength={500} onChange={(e) => setReadBackNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
